import { IConversationMessage } from "@next-platform/client-common-core";
import { TFunction } from "i18next";

import { getAdditionalImageLabels, getAdditionalTextLabels, RatingLabel } from "./constants";
import { ChatBubbleVariants, ChatRatings, MessageType, NsfwGateCta } from "./types";

export const getMaxWidthClasses = (variant?: ChatBubbleVariants) => {
  switch (variant) {
    case ChatBubbleVariants.USER:
      return "!w-fit min-w-[300px] max-mob:min-w-[200px] !max-w-[650px]";
    case ChatBubbleVariants.BOT:
      return "!max-w-[650px]";
    default:
      return "!max-w-[650px]";
  }
};

export const getBubbleStyles = (isUser: boolean) => {
  return isUser
    ? "rounded-[20px_4px_20px_20px] m-[0_10px_0_53px] bg-blumine-6 dark:bg-blumine-3"
    : "rounded-[4px_20px_20px_20px] m-[0_32px_0_10px] bg-gray-4";
};

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
export const getLabelsBasedOnType = (type: MessageType, t: TFunction): Record<string, RatingLabel[]> => {
  return type === MessageType.IMAGE ? getAdditionalImageLabels(t) : getAdditionalTextLabels(t);
};

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
export const getRatingSentMessage = (t: TFunction, ratingLabel: string) => {
  if (ratingLabel === ChatRatings.TERRIBLE || ratingLabel === ChatRatings.BAD) {
    return t("chat:chatBubbleRating.feedback.negative");
  }
  return t("chat:chatBubbleRating.feedback.positive");
};

export type ChatNsfwGate = {
  readonly isNsfwEnabled: boolean;
  readonly isNsfwChatAllowed: boolean;
  readonly isNsfwChatPaywalled: boolean;
  readonly isNsfwImageAllowed: boolean;
  readonly isAgeVerificationRequired: boolean;
};

type ChatGatedMessage = Partial<Pick<IConversationMessage, "content" | "image_url" | "is_nsfw">>;

export type ChatNsfwCtaModes = { readonly text?: NsfwGateCta; readonly image?: NsfwGateCta };

/** The calls to action an NSFW message's text and image are gated behind; a part the chat shows has none. */
export const getNsfwCtaModes = (message: ChatGatedMessage | undefined, gate: ChatNsfwGate): ChatNsfwCtaModes => {
  if (!message?.is_nsfw) {
    return {};
  }
  const modes: { text?: NsfwGateCta; image?: NsfwGateCta } = {};
  if (message.content) {
    if (!gate.isNsfwEnabled || !gate.isNsfwChatAllowed) {
      modes.text = gate.isNsfwChatPaywalled ? "paywall" : "disabled";
    } else if (gate.isAgeVerificationRequired) {
      modes.text = "verifyAge";
    }
  }
  if (message.image_url) {
    if (!gate.isNsfwEnabled || !gate.isNsfwImageAllowed) {
      modes.image = "disabled";
    } else if (gate.isAgeVerificationRequired) {
      modes.image = "verifyAge";
    }
  }
  return modes;
};

/** Whether the chat shows the message's image: the backend sent its url and neither gate replaces the bubble. */
export const isChatImageShown = (message: ChatGatedMessage | undefined, gate: ChatNsfwGate): boolean => {
  if (!message?.image_url) {
    return false;
  }
  const { text, image } = getNsfwCtaModes(message, gate);
  return text === undefined && image === undefined;
};
